import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormArray, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import * as ClientConstants from '../../../core/constants/client.constants';
import * as CountryConstants from '../../../core/constants/country.constants';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { share } from 'rxjs';
import { parseDateOnlyToLocal, toDateOnlyPayload } from '../../../core/utils/date-only.util';

@Component({
  selector: 'app-society-data-form',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, DropdownModule, InputTextModule, CheckboxModule, CalendarModule, AccordionModule],
  templateUrl: './society-data-form.component.html',
  styleUrl: './society-data-form.component.scss'
})
export class SocietyDataFormComponent {

  public clientConstants = ClientConstants;
  public countryConstants = CountryConstants;

  @Input() clientId: any;
  @Output() formIsDirty = new EventEmitter<boolean>();

  societyDataForm = this.fb.group({
    id: [],
    client: [],
    companyType: [''],
    isBigCompany: [''],
    startDate: [, Validators.required],
    endDate: [],
    startDateISOObligation: [],
    socialExerciseClosingDate: [],
    fractionatedPayment: [''],
    socialDomicileStartDate: [],
    acronym: [''],
    street: [''],
    number: [],
    stairs: [],
    floor: [],
    door: [],
    town: [''],
    postalCode: [],
    province: [''],
    codeCountry: [''],
    country: [''],
    countrySelector: [''], // Combined selector for UI
    cadastralReference: [''],
    socialDomicileEndDate: []
  });

  constitutionForm = this.fb.group({
    constitutions: this.fb.array([]),
  }); 

  socialObjectForm = this.fb.group({
    socialObjects: this.fb.array([]),
  });

  socialCapitalForm = this.fb.group({
    socialCapitals: this.fb.array([]),
  });

  capitalIncreaseForm = this.fb.group({
    capitalIncreases: this.fb.array([]),
  });

  capitalReductionForm = this.fb.group({
    capitalReductions: this.fb.array([]),
  });

  buySellForm = this.fb.group({
    buySells: this.fb.array([]),
  });

  shareholdersPartnersForm = this.fb.group({
    shareholdersPartners: this.fb.array([]),
  });

  administrationForm = this.fb.group({
    administrations: this.fb.array([]),
  });

  booksForm = this.fb.group({
      id: [],
      client: [],
      actsBookOrderNumber: [''],
      actsBookDate: [],
      partnersBookOrderNumber: [''],
      partnersBookDate: [],
      invCCABookOrderNumber: [''],
      invCCABookDate: [],
      diaryBookOrderNumber: [''],
      diaryBookDate: []
  });

  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    ) { 
      this.societyDataForm.valueChanges.subscribe(() => {
        if (this.societyDataForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.constitutionForm.valueChanges.subscribe(() => {
        if (this.constitutionForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.socialObjectForm.valueChanges.subscribe(() => {
        if (this.socialObjectForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.socialCapitalForm.valueChanges.subscribe(() => {
        if (this.socialCapitalForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.capitalIncreaseForm.valueChanges.subscribe(() => {
        if (this.capitalIncreaseForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.capitalReductionForm.valueChanges.subscribe(() => {
        if (this.capitalReductionForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.buySellForm.valueChanges.subscribe(() => {
        if (this.buySellForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.shareholdersPartnersForm.valueChanges.subscribe(() => {
        if (this.shareholdersPartnersForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.administrationForm.valueChanges.subscribe(() => {
        if (this.administrationForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.booksForm.valueChanges.subscribe(() => {
        if (this.booksForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      
      // When combined country selector changes, update iso2 and iso3 fields
      this.societyDataForm.get('countrySelector')?.valueChanges.subscribe(iso3 => {
        if (iso3) {
          const selectedCountry = CountryConstants.COUNTRIES_RAW.find(c => c.iso3 === iso3);
          if (selectedCountry) {
            this.societyDataForm.patchValue({ 
              codeCountry: selectedCountry.iso2,
              country: selectedCountry.iso3
            }, { emitEvent: false });
          }
        }
      });
    }

  async ngOnInit() {
    await this.getData();
      
  }

  async getData() {
    if (this.clientId) {
      let data = await this.clientsService.getClientSociety(this.clientId);
      if (data) {
        if (data.clientSociety) {
          data.clientSociety.startDate = parseDateOnlyToLocal(data.clientSociety.startDate);
        data.clientSociety.endDate = parseDateOnlyToLocal(data.clientSociety.endDate);
        data.clientSociety.startDateISOObligation = parseDateOnlyToLocal(data.clientSociety.startDateISOObligation);
        data.clientSociety.socialExerciseClosingDate = parseDateOnlyToLocal(data.clientSociety.socialExerciseClosingDate);
        data.clientSociety.socialDomicileStartDate = parseDateOnlyToLocal(data.clientSociety.socialDomicileStartDate);
        data.clientSociety.socialDomicileEndDate = parseDateOnlyToLocal(data.clientSociety.socialDomicileEndDate);
        
        // Initialize countrySelector based on existing data
        let countrySelector = '';
        if (data.clientSociety.country) {
          // If country is already iso3, use it
          const countryData = CountryConstants.COUNTRIES_RAW.find(c => c.iso3 === data.clientSociety.country);
          if (countryData) {
            countrySelector = countryData.iso3;
          } else {
            // If country is name, find by name
            const countryByName = CountryConstants.COUNTRIES_RAW.find(c => c.nameES === data.clientSociety.country);
            if (countryByName) {
              countrySelector = countryByName.iso3;
            }
          }
        } else if (data.clientSociety.codeCountry) {
          // If only codeCountry exists, find by iso2
          const countryByCode = CountryConstants.COUNTRIES_RAW.find(c => c.iso2 === data.clientSociety.codeCountry);
          if (countryByCode) {
            countrySelector = countryByCode.iso3;
          }
        }
        
        data.clientSociety.countrySelector = countrySelector;
        this.societyDataForm.patchValue(data.clientSociety);
        }
        

      if (data.constitution.length > 0) {
        data.constitution.forEach((constitution) => {
          constitution.documentDate = parseDateOnlyToLocal(constitution.documentDate);
          constitution.dealDate = parseDateOnlyToLocal(constitution.dealDate);
          constitution.inscriptionDate = parseDateOnlyToLocal(constitution.inscriptionDate);

          this.constitution.push(this.fb.group(constitution));
        });
      } else {
        this.addConstitution();
      }

      if (data.socialObject.length > 0) {
        data.socialObject.forEach((socialObject) => {
          socialObject.documentDate = parseDateOnlyToLocal(socialObject.documentDate);
          socialObject.dealDate = parseDateOnlyToLocal(socialObject.dealDate);
          socialObject.inscriptionDate = parseDateOnlyToLocal(socialObject.inscriptionDate);
          socialObject.startDate = parseDateOnlyToLocal(socialObject.startDate);
          socialObject.endDate = parseDateOnlyToLocal(socialObject.endDate);

          this.socialObject.push(this.fb.group(socialObject));
        });
      }

      if (data.socialCapital.length > 0) {
        data.socialCapital.forEach((socialCapital) => {

          this.socialCapital.push(this.fb.group(socialCapital));
        });
      }

      if (data.capitalIncrease.length > 0) {
        data.capitalIncrease.forEach((capitalIncrease) => {
          capitalIncrease.increaseDate = parseDateOnlyToLocal(capitalIncrease.increaseDate);
          capitalIncrease.dealDate = parseDateOnlyToLocal(capitalIncrease.dealDate);
          capitalIncrease.inscriptionDate = parseDateOnlyToLocal(capitalIncrease.inscriptionDate);

          this.capitalIncrease.push(this.fb.group(capitalIncrease));
        });
      }

      if (data.capitalReduction.length > 0) {
        data.capitalReduction.forEach((capitalReduction) => {
          capitalReduction.reductionDate = parseDateOnlyToLocal(capitalReduction.reductionDate);
          capitalReduction.dealDate = parseDateOnlyToLocal(capitalReduction.dealDate);
          capitalReduction.inscriptionDate = parseDateOnlyToLocal(capitalReduction.inscriptionDate);

          this.capitalReduction.push(this.fb.group(capitalReduction));
        });
      }

      if (data.buySell.length > 0) {
        data.buySell.forEach((buySell) => {
          buySell.documentDate = parseDateOnlyToLocal(buySell.documentDate);
          buySell.dealDate = parseDateOnlyToLocal(buySell.dealDate);
          buySell.contractDate = parseDateOnlyToLocal(buySell.contractDate);

          this.buySell.push(this.fb.group(buySell));
        });
      }

      if (data.shareholderPartner.length > 0) {
        data.shareholderPartner.forEach((shareholderPartner) => {
          shareholderPartner.startDate = parseDateOnlyToLocal(shareholderPartner.startDate);
          shareholderPartner.endDate = parseDateOnlyToLocal(shareholderPartner.endDate);

          this.shareholdersPartners.push(this.fb.group(shareholderPartner));
        });
      }

      if (data.administration.length > 0) {
        data.administration.forEach((administration) => {
          administration.startDate = parseDateOnlyToLocal(administration.startDate);
          administration.endDate = parseDateOnlyToLocal(administration.endDate);
          administration.startDocumentDate = parseDateOnlyToLocal(administration.startDocumentDate);
          administration.startDealDate = parseDateOnlyToLocal(administration.startDealDate);
          administration.startInscriptionDate = parseDateOnlyToLocal(administration.startInscriptionDate);
          administration.endDocumentDate = parseDateOnlyToLocal(administration.endDocumentDate);
          administration.endDealDate = parseDateOnlyToLocal(administration.endDealDate);
          administration.endInscriptionDate = parseDateOnlyToLocal(administration.endInscriptionDate);
          administration.personalDigitalCertificateCaducityDate = parseDateOnlyToLocal(administration.personalDigitalCertificateCaducityDate);
          this.administration.push(this.fb.group(administration));
        });
      }
      if (data.book.length > 0) {
        data.book[0].actsBookDate = parseDateOnlyToLocal(data.book[0].actsBookDate);
        data.book[0].partnersBookDate = parseDateOnlyToLocal(data.book[0].partnersBookDate);
        data.book[0].invCCABookDate = parseDateOnlyToLocal(data.book[0].invCCABookDate);
        data.book[0].diaryBookDate = parseDateOnlyToLocal(data.book[0].diaryBookDate);
        this.booksForm.patchValue(data.book[0]);
      }
      
    }
  }
  }

  get constitution(): FormArray {
    return this.constitutionForm.get('constitutions') as FormArray;
  }

  addConstitution() {
    const constitution = this.fb.group({
      id: [],
      client: [],
      documentConcept: [''],
      notary: [''],
      protocol: [''],
      documentDate: [],
      commercialRegisterProvince: [''],
      dealDate: [],
      inscriptionDate: [],
      tome: [''],
      sheet: [''],
      leaf: [''],
      section: [''],
      book: [''],
      inscriptionNumber: [''],
    });

    this.constitution.push(constitution);
  }

  get socialObject(): FormArray {
    return this.socialObjectForm.get('socialObjects') as FormArray;
  }

  addSocialObject() {
    const socialObject = this.fb.group({
      id: [],
      client: [],
      documentConcept: [''],
      notary: [''],
      protocol: [''],
      documentDate: [],
      commercialRegisterProvince: [''],
      dealDate: [],
      inscriptionDate: [''],
      tome: [''],
      sheet: [''],
      leaf: [''],
      section: [''],
      book: [''],
      inscriptionNumber: [''],
      socialObject: [''],
      principalActivityCNAE: [''],
      activityDetail: [''],
      principalActivityEpigraphIAE: [''],
      startDate: [],
      endDate: [],
    });

    this.socialObject.push(socialObject);
  }

  get socialCapital(): FormArray {
    return this.socialCapitalForm.get('socialCapitals') as FormArray;
  }

  addSocialCapital() {
    const socialCapital = this.fb.group({
      id: [],
      client: [],
      type: [''],
      totalActionsNumber: [''],
      nominalValueByAction: [''],
      totalSocialCapital: [''],
      actionsNumeration: [''],
      bonusEmission: [''],
      // TODO: Uncomment when observations field is added in backend -h
      // observations: [''],
    });

    this.socialCapital.push(socialCapital);
  }

  get capitalIncrease(): FormArray {
    return this.capitalIncreaseForm.get('capitalIncreases') as FormArray;
  }

  addCapitalIncrease() {
    const capitalIncrease = this.fb.group({
      id: [],
      client: [],
      documentConcept: [''],
      notary: [''],
      protocol: [''],
      documentDate: [],
      commercialRegisterProvince: [''],
      dealDate: [],
      inscriptionDate: [''],
      tome: [''],
      sheet: [''],
      leaf: [''],
      section: [''],
      book: [''],
      inscriptionNumber: [''],
      increaseDate: [''],
      increaseType: [''],
      observations: [''],
      capitalIncreaseSeries: this.fb.array([]),
    });

    this.capitalIncrease.push(capitalIncrease);
  }

  get capitalIncreaseSeries(): FormArray {
    return this.capitalIncrease.get('capitalIncreaseSeries') as FormArray;
  }

  addCapitalIncreaseSeries() { 
    const capitalIncreaseSeries = this.fb.group({
      numACCPartIncreased: [''],
      totalIncreasedNominal: [''],
      numACCPartAfterIncrease: [''],
      nominalValueAccPartAfterIncrease: [''],
      totalNominalValueAfterIncrease: [''],
    });

    this.capitalIncreaseSeries.push(capitalIncreaseSeries);
  }

  get capitalReduction(): FormArray {
    return this.capitalReductionForm.get('capitalReductions') as FormArray;
  }

  addCapitalReduction() {
    const capitalReduction = this.fb.group({
      id: [],
      client: [],
      documentConcept: [''],
      notary: [''],
      protocol: [''],
      documentDate: [],
      commercialRegisterProvince: [''],
      dealDate: [],
      inscriptionDate: [''],
      tome: [''],
      sheet: [''],
      leaf: [''],
      section: [''],
      book: [''],
      inscriptionNumber: [''],
      reductionDate: [''],
      reductionType: [''],
      observations: [''],
      series: this.fb.array([]),
    });

    this.capitalReduction.push(capitalReduction);
  }

  get capitalReductionSeries(): FormArray {
    return this.capitalIncrease.get('capitalReductionSeries') as FormArray;
  }

  addCapitalReductionSeries() { 
    const capitalReductionSeries = this.fb.group({
      numACCPartReduced: [''],
      totalIncreasedNominal: [''],
      numACCPartAfterReduction: [''],
      nominalValueAccPartAfterReduction: [''],
      totalNominalValueAfterReduction: [''],
    });

    this.capitalReductionSeries.push(capitalReductionSeries);
  }

  get buySell(): FormArray {
    return this.buySellForm.get('buySells') as FormArray;
  }

  addBuySell() {
    const buySell = this.fb.group({
      id: [],
      client: [],
      documentConcept: [''],
      notary: [''],
      protocol: [''],
      documentDate: [],
      dealDate: [],
      contractDate: [],
      buyerName: [''],
      actionsNumber: [],
      totalNominal: [],
      actionsNumeration: [''],
      emissionPrime: [],
      buySellPrice: [],
      sellerName: [''],


    });

    this.buySell.push(buySell);
  }

  get shareholdersPartners(): FormArray {
    return this.shareholdersPartnersForm.get('shareholdersPartners') as FormArray;
  }

  addShareholdersPartners() {
    const shareholdersPartners = this.fb.group({
      id: [],
      client: [],
      name: [''],
      nif: [''],
      resident: [''],
      domicile: [''],
      postalCode: [''],
      town: [''],
      province: [''],
      startDate: [],
      representativeName: [''],
      representativeNif: [''],
      actionsNumber: [''],
      actionsNumeration: [''],
      percentage: [''],
      directControl: [''],
      indirectControl: [''],
      familiar: [''],
      worker: [''],
      administrationMember: [''],
      socialSecurityRegime: [''],
      framing: [''],
      autonomousInChargeOf: [''],
      iban: [''],
      monthlyNetReturn: [''],
      mutuaACC: [''],
      mutuaCC: [''],
      totalNominalValue: [''],
      generalMeetingPresident: [''],
      generalMeetingSecretary: [''],
      endDate: [],

    });

    this.shareholdersPartners.push(shareholdersPartners);
  }

  get administration(): FormArray {
    return this.administrationForm.get('administrations') as FormArray;
  }

  addAdministration() {
    const administration = this.fb.group({
      id: [],
      client: [],
      administrationSystem: [''],
      name: [''],
      nif: [''],
      address: [''],
      postalCode: [''],
      town: [''],
      province: [''],
      position: [''],
      startDate: [],
      startNotary: [''],
      startProtocol: [''],
      startDocumentDate: [],
      startMercantileRegisterProvince: [''],
      startDealDate: [],
      startInscriptionDate: [],
      startTome: [''],
      startSheet: [''],
      startLeaf: [''],
      startSection: [''],
      startBook: [''],
      startInscriptionNumber: [''],
      endDate: [],
      endNotary: [''],
      endProtocol: [''],
      endDocumentDate: [''],
      endMercantileRegisterProvince: [''],
      endDealDate: [],
      endInscriptionDate: [],
      endTome: [''],
      endSheet: [''],
      endLeaf: [''],
      endSection: [''],
      endBook: [''],
      endInscriptionNumber: [''],
      representativeName: [''],
      representativeNif: [''],
      retribution: [''],
      retributionKey: [''],
      retributionImport: [''],
      retributionInStatutes: [''],
      retributionStartRetributionExercise: [''],
      retributionContract: [''],
      retributionAct: [''],
      regime: [''],
      framing: [''],
      iban: [''],
      autonomousInChargeOf: [''],
      monthlyNetReturn: [''],
      mutuaACC: [''],
      mutuaCC: [''],
      personalDigitalCertificate: [''],
      personalDigitalCertificateCaducityDate: [],
      signsCCAA: [''],
      certifiesCCAA: [''],
      signsIS: [''],
      certifiesIS: [''],
      signsLaborContracts: [''],
      signsLaborDocuments: [''],
    });

    this.administration.push(administration);
  }

  async save() {
    const { countrySelector, ...societyData } = this.societyDataForm.value;
    societyData.client = Number(this.clientId);
    if (!societyData.id) {
      delete societyData['id'];
    }
    await this.clientsService.saveClientSociety({
      ...societyData,
      startDate: toDateOnlyPayload(societyData.startDate),
      endDate: toDateOnlyPayload(societyData.endDate),
      startDateISOObligation: toDateOnlyPayload(societyData.startDateISOObligation),
      socialExerciseClosingDate: toDateOnlyPayload(societyData.socialExerciseClosingDate),
      socialDomicileStartDate: toDateOnlyPayload(societyData.socialDomicileStartDate),
      socialDomicileEndDate: toDateOnlyPayload(societyData.socialDomicileEndDate),
    });

      for (let i = 0; i < this.constitution.length; i++) {
        if(this.constitution.at(i).dirty) {
          if(!this.constitution.at(i).value.id) {
          this.constitution.at(i).value.client = Number(this.clientId);
          }
          if (!this.constitution.at(i).value.id) {
            delete this.constitution.value['id'];
          }
          const c = this.constitution.at(i).value;
          await this.clientsService.saveClientConstitution({
            ...c,
            documentDate: toDateOnlyPayload(c.documentDate),
            dealDate: toDateOnlyPayload(c.dealDate),
            inscriptionDate: toDateOnlyPayload(c.inscriptionDate),
          });
        }
      }
    
      for (let i = 0; i < this.socialObject.length; i++) {
        if(this.socialObject.at(i).dirty) {
          if(!this.socialObject.at(i).value.id) {
            this.socialObject.at(i).value.client = Number(this.clientId);
          }        
          if (!this.socialObject.at(i).value.id) {
            delete this.socialObject.value['id'];
          }
          const so = this.socialObject.at(i).value;
          await this.clientsService.saveClientSocialObject({
            ...so,
            documentDate: toDateOnlyPayload(so.documentDate),
            dealDate: toDateOnlyPayload(so.dealDate),
            inscriptionDate: toDateOnlyPayload(so.inscriptionDate),
            startDate: toDateOnlyPayload(so.startDate),
            endDate: toDateOnlyPayload(so.endDate),
          });
        }
      }
    
      // for (let i = 0; i < this.socialCapital.length; i++) {
      //   if(this.socialCapital.at(i).dirty) {
      //     if(!this.socialCapital.at(i).value.client) {
      //       this.socialCapital.at(i).value.client = Number(this.clientId);
      //     }
      //     if (!this.socialCapital.at(i).value.id) {
      //       delete this.socialCapital.value['id'];
      //     }
      //     await this.clientsService.saveClientSocialCapital(this.socialCapital.at(i).value);
      //   }
      // }
    
      for (let i = 0; i < this.capitalIncrease.length; i++) {
        if(this.capitalIncrease.at(i).dirty) {
          if(!this.capitalIncrease.at(i).value.client) {
            this.capitalIncrease.at(i).value.client = Number(this.clientId);
          }
          if (!this.capitalIncrease.at(i).value.id) {
            delete this.capitalIncrease.value['id'];
          }
          const ci = this.capitalIncrease.at(i).value;
          await this.clientsService.saveClientCapitalIncrease({
            ...ci,
            increaseDate: toDateOnlyPayload(ci.increaseDate),
            dealDate: toDateOnlyPayload(ci.dealDate),
            inscriptionDate: toDateOnlyPayload(ci.inscriptionDate),
          });
        }
      }
    
      for (let i = 0; i < this.capitalReduction.length; i++) {
        if(this.capitalReduction.at(i).dirty) {
          if(!this.capitalReduction.at(i).value.client) {
            this.capitalReduction.at(i).value.client = Number(this.clientId);
          }
          if (!this.capitalReduction.at(i).value.id) {
            delete this.capitalReduction.value['id'];
          }
          const cr = this.capitalReduction.at(i).value;
          await this.clientsService.saveClientCapitalReduction({
            ...cr,
            reductionDate: toDateOnlyPayload(cr.reductionDate),
            dealDate: toDateOnlyPayload(cr.dealDate),
            inscriptionDate: toDateOnlyPayload(cr.inscriptionDate),
          });
        }
      }
    
      for (let i = 0; i < this.buySell.length; i++) {
        if(this.buySell.at(i).dirty) {
          if(!this.buySell.at(i).value.client) {
            this.buySell.at(i).value.client = Number(this.clientId);
          }
          if (!this.buySell.at(i).value.id) {
            delete this.buySell.value['id'];
          }
          const bs = this.buySell.at(i).value;
          await this.clientsService.saveClientBuySells({
            ...bs,
            documentDate: toDateOnlyPayload(bs.documentDate),
            dealDate: toDateOnlyPayload(bs.dealDate),
            contractDate: toDateOnlyPayload(bs.contractDate),
          });
        }
      }
    
      for (let i = 0; i < this.shareholdersPartners.length; i++) {
        if(this.shareholdersPartners.at(i).dirty) {
          if(!this.shareholdersPartners.at(i).value.client) {
            this.shareholdersPartners.at(i).value.client = Number(this.clientId);
          }
          if (!this.shareholdersPartners.at(i).value.id) {
            delete this.shareholdersPartners.value['id'];
          }
          const sp = this.shareholdersPartners.at(i).value;
          await this.clientsService.saveClientShareholders({
            ...sp,
            startDate: toDateOnlyPayload(sp.startDate),
            endDate: toDateOnlyPayload(sp.endDate),
          });
        }
      }
    
      // for (let i = 0; i < this.administration.length; i++) {
      //   if(this.administration.at(i).dirty) {
      //     if(!this.administration.at(i).value.client) {
      //       this.administration.at(i).value.client = Number(this.clientId);
      //     }
      //     if (!this.administration.at(i).value.id) {
      //       delete this.administration.value['id'];
      //     }
      //     await this.clientsService.saveClientAdministrators(this.administration.at(i).value);
      //   }
      // }
      if(this.booksForm.dirty) {
        this.booksForm.value.client = Number(this.clientId);
        if(!this.booksForm.value.id) {
          delete this.booksForm.value['id'];
        }
        const booksVal = this.booksForm.value;
        await this.clientsService.saveClientBook({
          ...booksVal,
          actsBookDate: toDateOnlyPayload(booksVal.actsBookDate),
          partnersBookDate: toDateOnlyPayload(booksVal.partnersBookDate),
          invCCABookDate: toDateOnlyPayload(booksVal.invCCABookDate),
          diaryBookDate: toDateOnlyPayload(booksVal.diaryBookDate),
        });
      }
    await this.getData();
  }
}
